import { useEffect, useState } from "react";
import { Loader2, Trash2, Check, Circle } from "lucide-react";
import { adminApi } from "../../lib/adminApi";
import { ConfirmModal } from "./ConfirmModal";

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("fr-BE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

// 'YYYY-MM-DD' -> 'DD/MM/YYYY' (RDV date field)
const fmtDay = (s) => {
  if (!s) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : s;
};

const CONFIG = {
  devis: {
    title: "Demandes de devis",
    load: () => adminApi.listDevis(),
    update: (id, s) => adminApi.updateDevis(id, s),
    remove: (id) => adminApi.deleteDevis(id),
    heading: (r) => `${r.prenom || ""} ${r.nom || ""}`.trim() || "Demande",
    rows: (r) => [
      ["E-mail", r.email],
      ["Téléphone", r.phone],
      ["Code postal", r.cp],
      ["Habitation", r.habitation],
      ["Menuiseries", (r.menuiseries || []).join(", ")],
      ["Matériau", r.material],
      ["Vitrage", r.glazing],
      ["Précisions", r.details],
    ],
  },
  rdv: {
    title: "Rendez-vous",
    load: () => adminApi.listRdv(),
    update: (id, s) => adminApi.updateRdv(id, s),
    remove: (id) => adminApi.deleteRdv(id),
    heading: (r) => `${fmtDay(r.date)} · ${r.slot || ""}`.trim(),
    rows: (r) => [
      ["Nom", `${r.firstName || ""} ${r.lastName || ""}`.trim()],
      ["E-mail", r.email],
      ["Téléphone", r.phone],
      ["Adresse", r.address],
      ["Commentaire", r.comment],
    ],
  },
};

export const SubmissionsPanel = ({ type, onChange }) => {
  const cfg = CONFIG[type];
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmId, setConfirmId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setItems(await cfg.load());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const toggle = async (r) => {
    const next = r.status === "traite" ? "nouveau" : "traite";
    await cfg.update(r.id, next);
    setItems((list) => list.map((it) => (it.id === r.id ? { ...it, status: next } : it)));
    if (onChange) onChange();
  };

  const remove = async () => {
    if (!confirmId) return;
    setDeleting(true);
    try {
      await cfg.remove(confirmId);
      setItems((list) => list.filter((it) => it.id !== confirmId));
      setConfirmId(null);
      if (onChange) onChange();
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div data-testid={`submissions-panel-${type}`}>
      <h2 className="font-display text-2xl font-medium mb-6">{cfg.title}</h2>

      {loading ? (
        <div className="flex items-center gap-2 text-white/50 text-sm"><Loader2 size={16} className="animate-spin" /> Chargement…</div>
      ) : items.length === 0 ? (
        <p className="text-white/45 text-sm" data-testid={`submissions-empty-${type}`}>Aucune demande pour le moment.</p>
      ) : (
        <div className="grid gap-4">
          {items.map((r) => {
            const done = r.status === "traite";
            return (
              <div key={r.id} data-testid={`submission-${r.id}`} className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2.5">
                      <p className="font-medium">{cfg.heading(r)}</p>
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${done ? "bg-accent-yellow/15 text-accent-yellow" : "bg-white/10 text-white/60"}`}>
                        {done ? "Traité" : "Nouveau"}
                      </span>
                      {r.lang && <span className="text-[10px] uppercase tracking-wider text-white/30">{r.lang}</span>}
                    </div>
                    <p className="text-xs text-white/35 mt-1">{fmtDate(r.created_at)}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => toggle(r)} data-testid={`submission-toggle-${r.id}`} className="p-2.5 rounded-lg border border-white/12 text-white/70 hover:text-accent-yellow hover:border-accent-yellow transition-colors" title={done ? "Marquer comme nouveau" : "Marquer comme traité"}>
                      {done ? <Circle size={15} /> : <Check size={15} />}
                    </button>
                    <button onClick={() => setConfirmId(r.id)} data-testid={`submission-delete-${r.id}`} className="p-2.5 rounded-lg border border-white/12 text-white/70 hover:text-red-400 hover:border-red-400/50 transition-colors"><Trash2 size={15} /></button>
                  </div>
                </div>

                <dl className="mt-4 grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
                  {cfg.rows(r).filter(([, v]) => v).map(([k, v]) => (
                    <div key={k} className="flex gap-2">
                      <dt className="text-white/40 shrink-0">{k} :</dt>
                      <dd className="text-white/75 break-words min-w-0">{v}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            );
          })}
        </div>
      )}

      <ConfirmModal
        open={!!confirmId}
        title="Supprimer cette demande ?"
        message="Cette action est définitive."
        confirmLabel="Supprimer"
        loading={deleting}
        onConfirm={remove}
        onClose={() => setConfirmId(null)}
      />
    </div>
  );
};
